/**
 * @file OpenTelemetry Failed HTTP Response Handler
 * @description Handles failed HTTP responses by logging, tracing, and recording OpenTelemetry metrics.
 */

import { logAndTraceErrorResponse } from './logAndTraceErrorResponse';
import { withSafelyExecute } from '../../utils/withSafelyExecute';
import { otlHandleHttpResponseMetrics } from '../../utils/otlHandleHttpResponseMetrics';
import { getDuration } from '../../utils';

/**
 * Handles a failed HTTP response by logging, tracing the error, and recording OpenTelemetry metrics.
 *
 * @param {Object} error - The error object from Axios.
 * @param {Object} error.config - The Axios request configuration.
 * @param {Object} error.config.span - The OpenTelemetry span associated with the request.
 * @param {Object} error.config.context - The OpenTelemetry context associated with the request.
 * @param {Object} [error.config.logData] - Additional data to include in the log.
 * @param {string} error.config.url - The URL of the request.
 * @param {string} error.config.method - The HTTP method (GET, POST, etc.).
 * @param {Object} error.config.metadata - Metadata containing the request's start time.
 * @param {number} error.config.metadata.startTime - The timestamp when the request started.
 * @param {Object} [error.response] - The Axios response object, if available.
 * @param {number} [error.response.status] - The HTTP status code.
 * @param {string} [error.response.statusText] - The HTTP status text.
 */
function otlHandleFailedResponse(error) {
    const { span, logData, url, method } = error.config || {};
    const { status, statusText } = error.response || {};

    if (!span) {
        return;
    }

    logAndTraceErrorResponse({
        span,
        url,
        logData,
        statusText,
        status,
        error: error.message,
    });
    otlHandleHttpResponseMetrics({ method, url, duration: getDuration(span), status });
}

export default withSafelyExecute(otlHandleFailedResponse);
